import {
  formatMoney,
  ownPeriodPhrase,
  ownPeriodSpend,
  type OwnPeriodSpend,
} from '@/features/workspace-usage';
import type {
  LimitSource,
  OrganizationConcurrencyView,
  OrganizationMemoryView,
  OrganizationUsageSummary,
  PeriodBounds,
  PeriodType,
  WorkflowLimitRow,
  WorkspaceLimitRow,
  WorkspaceLimitRowThreshold,
} from '@/lib/api/generated/model';

import type { MemoryUnit } from './memoryLimit';

/*
 * The Limits page, read one workspace at a time (spec 117).
 *
 * Three organization reads answer three questions — what each workspace has spent against
 * its limit, which of its workflows are capped, and how many runs it may have at once —
 * and the page used to show them as three tables. This joins them into one row per
 * workspace, so "why is this workspace held back?" is answered by one row. Pure: the
 * page, the filters and the tests all read the same rows.
 */

/** Why a workspace needs a look, most urgent first (R2). */
export type AttentionReason =
  'spend-exhausted' | 'run-full' | 'spend-approaching' | 'waiting';

const REASON_RANK: Record<AttentionReason, number> = {
  'spend-exhausted': 0,
  'run-full': 1,
  'spend-approaching': 2,
  waiting: 3,
};

/** A row that needs nothing sorts after every reason. */
const NO_REASON_RANK = 9;

export const REASON_WORDS: Record<AttentionReason, string> = {
  'spend-exhausted': 'At spend limit',
  'run-full': 'At run limit',
  'spend-approaching': 'Near spend limit',
  waiting: 'Runs waiting',
};

export interface WorkspaceRuns {
  limit: number;
  running: number;
  queued: number;
  source: LimitSource;
  clamped: boolean;
}

/** A workspace's memory amount (spec 149), in the unit the service reported it in. */
export interface WorkspaceMemory {
  amountMib: number;
  unit: MemoryUnit;
  source: LimitSource;
}

export interface WorkspaceLimitsRow {
  workspaceId: string;
  name: string;
  /** Spent this period, as the service writes money; "0" when it recorded none. */
  spend: string;
  /** The limit that applies, or `null` for none. */
  spendLimit: string | null;
  spendCustom: boolean;
  spendPeriod: PeriodType | null;
  spendThreshold: WorkspaceLimitRowThreshold | null;
  /**
   * What the limit's own period spent and how to say it ("today"), when the limit is
   * measured over another period than the one read (spec 237). `null` when it is the
   * same one: `spend` is then the limit's figure.
   */
  limitOwn: OwnPeriodSpend | null;
  /** The period being read, which a workflow cap's own period is said against. */
  readPeriod: PeriodBounds;
  currency: string;
  /** `null` when the concurrency read is unavailable (an older service). */
  runs: WorkspaceRuns | null;
  /**
   * `null` is the one representation of "nothing is known about this workspace's
   * memory" — whether the whole read is absent (an older service) or only this row is.
   */
  memory: WorkspaceMemory | null;
  /** This workspace's capped workflows. */
  caps: WorkflowLimitRow[];
  reasons: AttentionReason[];
  rank: number;
  /** The spend read's own row, for the rules that take it whole (stop forecasts). */
  usageRow: WorkspaceLimitRow | null;
  /**
   * Gone, with its spend still counted (spec 146): shown under its last name, with no
   * limit and nothing to change.
   */
  deleted: boolean;
}

export interface LimitsSources {
  usage: OrganizationUsageSummary;
  caps: readonly WorkflowLimitRow[];
  /** `null` when the service has no such read (404). */
  concurrency: OrganizationConcurrencyView | null;
  /** `null` when the service has no such read (404). */
  memory: OrganizationMemoryView | null;
}

function reasonsOf(
  threshold: WorkspaceLimitRowThreshold | null,
  runs: WorkspaceRuns | null,
): AttentionReason[] {
  const reasons: AttentionReason[] = [];
  if (threshold === 'exhausted') reasons.push('spend-exhausted');
  const full = runs !== null && runs.running >= runs.limit;
  if (full) reasons.push('run-full');
  if (threshold === 'approaching') reasons.push('spend-approaching');
  // Waiting with room of its own means the installation is what holds it back.
  if (!full && runs !== null && runs.queued > 0) reasons.push('waiting');
  return reasons;
}

function byName(a: WorkspaceLimitsRow, b: WorkspaceLimitsRow): number {
  return (
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
    a.workspaceId.localeCompare(b.workspaceId)
  );
}

/**
 * One row per workspace any of the reads names, in the default order: whatever needs
 * attention first, then by name (FR-005).
 *
 * A workspace missing from one read (created a moment ago, deleted mid-refresh, or never
 * charged) takes a stated default for that part rather than an error: no spend, the
 * organization's limit, no caps.
 */
export function workspaceLimitRows({
  usage,
  caps,
  concurrency,
  memory,
}: LimitsSources): WorkspaceLimitsRow[] {
  const currency = usage.spend.currency ?? 'USD';
  const spendById = new Map(usage.workspaces.map((w) => [w.workspace_id, w]));
  const runsById = new Map(
    (concurrency?.workspaces ?? []).map((w) => [w.workspace_id, w]),
  );
  const memoryById = new Map(
    (memory?.workspaces ?? []).map((w) => [w.workspace_id, w]),
  );
  const capsById = new Map<string, WorkflowLimitRow[]>();
  for (const cap of caps) {
    const list = capsById.get(cap.workspace_id) ?? [];
    list.push(cap);
    capsById.set(cap.workspace_id, list);
  }

  const ids = new Set<string>([
    ...spendById.keys(),
    ...runsById.keys(),
    ...capsById.keys(),
  ]);

  const rows: WorkspaceLimitsRow[] = [];
  for (const id of ids) {
    const spend = spendById.get(id) ?? null;
    const run = runsById.get(id) ?? null;
    const mem = memoryById.get(id) ?? null;
    const ownCaps = capsById.get(id) ?? [];
    const runs: WorkspaceRuns | null = run
      ? {
          limit: run.limit,
          running: run.running,
          queued: run.queued,
          source: run.source,
          clamped: run.clamped,
        }
      : null;
    const workspaceMemory: WorkspaceMemory | null = mem
      ? {
          amountMib: mem.limit_mib,
          unit: mem.unit,
          source: mem.source,
        }
      : null;
    const threshold = spend?.threshold ?? null;
    const reasons = reasonsOf(threshold, runs);
    rows.push({
      workspaceId: id,
      name:
        spend?.workspace_name ??
        run?.name ??
        ownCaps[0]?.workspace_name ??
        'Unknown workspace',
      spend: spend?.spend ?? '0',
      spendLimit: spend ? spend.limit : (usage.default_limit ?? null),
      spendCustom: spend?.is_override ?? false,
      spendPeriod: spend?.period_type ?? null,
      spendThreshold: threshold,
      limitOwn: ownPeriodSpend(
        spend?.limit_period,
        usage.period,
        spend?.limit_consumed_amount,
      ),
      readPeriod: usage.period,
      currency: spend?.currency ?? currency,
      runs,
      memory: workspaceMemory,
      caps: ownCaps,
      reasons,
      rank: reasons.length > 0 ? REASON_RANK[reasons[0]] : NO_REASON_RANK,
      usageRow: spend,
      deleted: spend?.deleted ?? false,
    });
  }

  return rows.sort((a, b) => a.rank - b.rank || byName(a, b));
}

export type LimitsFilter =
  'all' | 'near-spend' | 'run-full' | 'waiting' | 'custom' | 'caps';

export const LIMITS_FILTERS: readonly LimitsFilter[] = [
  'all',
  'near-spend',
  'run-full',
  'waiting',
  'custom',
  'caps',
];

export const FILTER_WORDS: Record<LimitsFilter, string> = {
  all: 'All',
  'near-spend': 'Near spend limit',
  'run-full': 'At run limit',
  waiting: 'Waiting now',
  custom: 'Custom limits',
  caps: 'Has workflow caps',
};

/** The run-specific filters, which an older service cannot answer (R9). */
export const RUN_FILTERS: ReadonlySet<LimitsFilter> = new Set([
  'run-full',
  'waiting',
]);

export function matchesFilter(
  row: WorkspaceLimitsRow,
  filter: LimitsFilter,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'near-spend':
      return (
        row.spendThreshold === 'approaching' ||
        row.spendThreshold === 'exhausted'
      );
    case 'run-full':
      return row.runs !== null && row.runs.running >= row.runs.limit;
    case 'waiting':
      return row.runs !== null && row.runs.queued > 0;
    case 'custom':
      return (
        row.spendCustom ||
        row.runs?.source === 'workspace' ||
        row.memory?.source === 'workspace'
      );
    case 'caps':
      return row.caps.length > 0;
  }
}

export function matchesQuery(row: WorkspaceLimitsRow, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase();
  return needle === '' || row.name.toLocaleLowerCase().includes(needle);
}

export type LimitsSortKey = 'name' | 'spend' | 'runs';
export interface LimitsSort {
  key: LimitsSortKey;
  dir: 'asc' | 'desc';
}

/**
 * The spend a workspace's limit is judged by: its own period's when that differs from
 * the period read (a Daily limit read in October), else the period's (spec 237).
 */
export function limitSpendOf(row: WorkspaceLimitsRow): string {
  return row.limitOwn?.spend ?? row.spend;
}

/**
 * A workflow cap's spend against its amount: "$3.00 today of $5.00" for a cap measured
 * over another period than the one read, "$4.23 of $10.00" otherwise (spec 237).
 */
export function capSpendText(
  cap: WorkflowLimitRow,
  readPeriod: PeriodBounds,
  currency: string,
): string {
  const limit = formatMoney(cap.limit, currency);
  const phrase = ownPeriodPhrase(cap.limit_period, readPeriod);
  return phrase === null
    ? `${formatMoney(cap.spend, currency)} of ${limit}`
    : `${formatMoney(cap.limit_consumed_amount, currency)} ${phrase} of ${limit}`;
}

/** Share of the limit spent; a workspace with no limit sorts below every share. */
function spendShare(row: WorkspaceLimitsRow): number {
  const limit = row.spendLimit === null ? 0 : Number(row.spendLimit);
  return limit > 0 ? Number(limitSpendOf(row)) / limit : -1;
}

/** Load against the run limit, waiting runs included. */
function runLoad(row: WorkspaceLimitsRow): number {
  return row.runs && row.runs.limit > 0
    ? (row.runs.running + row.runs.queued) / row.runs.limit
    : -1;
}

/** A column's order; without one, the default (attention, then name). */
export function sortRows(
  rows: readonly WorkspaceLimitsRow[],
  sort?: LimitsSort,
): WorkspaceLimitsRow[] {
  const copy = [...rows];
  if (!sort) return copy.sort((a, b) => a.rank - b.rank || byName(a, b));
  const sign = sort.dir === 'asc' ? 1 : -1;
  const value =
    sort.key === 'spend' ? spendShare : sort.key === 'runs' ? runLoad : null;
  return copy.sort((a, b) => {
    if (value === null) return sign * byName(a, b);
    // Ties read A to Z whichever way the column runs: a reversed tie-break would
    // reorder equal rows every time the direction flipped.
    return sign * (value(a) - value(b)) || byName(a, b);
  });
}

export interface LimitsPolicy {
  /** The organization's spend default, or `null` for none. */
  spendDefault: string | null;
  /** The organization's runs default, or `null` (the installation fallback applies). */
  runsDefault: number | null;
  fallback: number | null;
  ceiling: number | null;
  followingSpend: number;
  followingRuns: number;
  /** The organization's memory default and its unit, displayed and never written (spec 149 FR-006a). */
  memoryDefault: number | null;
  memoryDefaultUnit: MemoryUnit | null;
  memoryFallback: number | null;
  memoryMinimum: number | null;
  followingMemory: number;
}

/** The organization-wide rules, and how many workspaces follow each (FR-014). */
export function policyOf({
  usage,
  concurrency,
  memory,
  rows,
}: {
  usage: OrganizationUsageSummary;
  concurrency: OrganizationConcurrencyView | null;
  memory: OrganizationMemoryView | null;
  rows: readonly WorkspaceLimitsRow[];
}): LimitsPolicy {
  return {
    spendDefault: usage.default_limit ?? null,
    runsDefault: concurrency?.default ?? null,
    fallback: concurrency?.fallback ?? null,
    ceiling: concurrency?.ceiling ?? null,
    followingSpend: rows.filter((r) => !r.spendCustom).length,
    followingRuns: rows.filter((r) => r.runs && r.runs.source !== 'workspace')
      .length,
    memoryDefault: memory?.default_mib ?? null,
    memoryDefaultUnit: memory?.default_unit ?? null,
    memoryFallback: memory?.fallback_mib ?? null,
    memoryMinimum: memory?.minimum_mib ?? null,
    followingMemory: rows.filter(
      (r) => r.memory && r.memory.source !== 'workspace',
    ).length,
  };
}
